import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  isThemePreference,
  prefersDark,
  readStoredTheme,
  writeStoredTheme,
  type ResolvedTheme,
  type ThemePreference,
} from './theme.js';

/**
 * Theme state of the app (APA-01, APA-03). The provider keeps the device preference and applies
 * it to `<html>`; `SyncedTheme` (mounted once the local database is open) brings in the synced
 * preference of the user and writes it back when it changes.
 */

export interface ThemeState {
  /** What the user chose: light, dark or follow the system. */
  preference: ThemePreference;
  /** What is actually painted right now. */
  resolved: ResolvedTheme;
  /** The choice applies only to this browser: the synced preference must not change. */
  deviceOnly: boolean;
  setTheme: (preference: ThemePreference, options?: { deviceOnly?: boolean }) => void;
  /** Applies the preference that arrives from the user's synced row. */
  receiveSyncedTheme: (preference: string | null | undefined) => void;
}

const ThemeContext = createContext<ThemeState | null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const stored = useMemo(() => readStoredTheme(), []);
  const [preference, setPreference] = useState<ThemePreference>(stored?.preference ?? 'system');
  const [deviceOnly, setDeviceOnly] = useState(stored?.deviceOnly ?? false);
  /** A preference chosen in this browser; the first synced row must not override it. */
  const hadStoredPreference = useRef(stored !== undefined);
  const lastSynced = useRef<ThemePreference | undefined>(undefined);
  const [systemDark, setSystemDark] = useState(prefersDark);
  const resolved: ResolvedTheme =
    preference === 'system' ? (systemDark ? 'dark' : 'light') : preference;

  // The theme lives in the DOM: `<html data-theme>` is what the token variables key off.
  useEffect(() => {
    document.documentElement.dataset.theme = resolved;
  }, [resolved]);

  // Follow the operating system live (no reload) while the preference is `system`.
  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent) => setSystemDark(event.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  const setTheme = useCallback<ThemeState['setTheme']>((next, options) => {
    const onlyHere = options?.deviceOnly ?? false;
    // A choice made here now counts as this browser's own: a synced row arriving later with an
    // older value (the first sync can land after the user already picked) must not undo it.
    hadStoredPreference.current = true;
    setPreference(next);
    setDeviceOnly(onlyHere);
    writeStoredTheme({ preference: next, deviceOnly: onlyHere });
  }, []);

  const receiveSyncedTheme = useCallback<ThemeState['receiveSyncedTheme']>(
    (value) => {
      if (deviceOnly || !isThemePreference(value)) return;
      const previous = lastSynced.current;
      lastSynced.current = value;
      // The first row that arrives never overrides a choice made in this browser: it can be older
      // than the local one (the local database is rebuilt from the server on every start). From
      // then on, only a *change* of the synced value is the user choosing on another device; the
      // same value seen again (a re-render, a remount) must not undo a local choice.
      if (previous === undefined && hadStoredPreference.current) return;
      if (previous === value) return;
      setPreference(value);
      // Cached for the inline script, so the next start paints the right theme straight away.
      writeStoredTheme({ preference: value, deviceOnly: false });
    },
    [deviceOnly],
  );

  const state = useMemo<ThemeState>(
    () => ({ preference, resolved, deviceOnly, setTheme, receiveSyncedTheme }),
    [preference, resolved, deviceOnly, setTheme, receiveSyncedTheme],
  );
  return <ThemeContext.Provider value={state}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeState {
  const state = useContext(ThemeContext);
  if (!state) throw new Error('useTheme necesita ThemeProvider');
  return state;
}
